/**
 * Input/output contracts for the Notification bulk callables.
 *
 * Single-notification creation goes through direct Firestore writes from
 * the admin compose form (notificationService.createNotification). The
 * bulk path lives here because every row references a named audience by
 * name (resolves against this event's audiences) and converts an
 * event-local datetime string to a UTC Timestamp.
 *
 * Audience targeting (ADR-120 + ADR-137): each row's `audience` is a
 * single audience-name string. The callable resolves it case-insensitively
 * against the event's audiences (custom groups and the system role
 * audiences like "All Members"). Multi-audience targeting is not
 * supported via import - those go through the admin compose form which
 * can compose anonymous audiences on the fly.
 *
 * `scheduleMode === 'now'` writes `status: 'sent'` (sendNotification CF
 * fires immediately on the audience). `scheduleMode === 'scheduled'`
 * writes `status: 'scheduled'` plus a `scheduledAt` UTC timestamp; the
 * processScheduledNotifications cron flips status to 'sent' when due.
 *
 * `deepLink` is stored verbatim on the notification doc - same shape as
 * the admin compose form.
 */

import { z } from 'zod';

export const bulkCreateNotificationsRowSchema = z
  .object({
    title: z.string().min(1).max(200),
    body: z.string().min(1).max(2000),
    /** Audience name reference (resolved case-insensitively per event). */
    audience: z.string().min(1).max(200),
    /** One or more delivery channels. */
    channels: z.array(z.enum(['push', 'email'])).min(1),
    scheduleMode: z.enum(['now', 'scheduled']),
    /** ISO-8601 datetime string (UTC). Required when scheduleMode='scheduled'. */
    scheduledAt: z.string().nullable(),
    /** Free-text deep link reference, stored verbatim on the notification doc. */
    deepLink: z.string().nullable(),
    /**
     * Optional email subject line; falls back to `title` at delivery
     * when null. Ignored when `channels` doesn't include 'email'.
     */
    emailSubject: z.string().nullable(),
    /**
     * Optional HTML email body; falls back to `body` at delivery when
     * null. Ignored when `channels` doesn't include 'email'.
     */
    emailBodyHtml: z.string().nullable(),
  })
  .strict();

export type BulkCreateNotificationsRow = z.infer<
  typeof bulkCreateNotificationsRowSchema
>;

export const bulkCreateNotificationsInputSchema = z
  .object({
    eventId: z.string().min(1),
    rows: z.array(bulkCreateNotificationsRowSchema).min(1).max(500),
  })
  .strict();

export type BulkCreateNotificationsInput = z.infer<
  typeof bulkCreateNotificationsInputSchema
>;

export type BulkCreateNotificationsRowStatus =
  | 'created'
  | 'unknown_audience'
  | 'invalid_schedule'
  | 'failed';

export interface BulkCreateNotificationsRowResult {
  index: number;
  title: string;
  status: BulkCreateNotificationsRowStatus;
  /** New notification document ID, populated when status === 'created'. */
  notificationId?: string;
  /** Resolved audience document ID, populated when status === 'created'. */
  audienceId?: string;
  /** Populated for any non-created status. */
  message?: string;
}

export interface BulkCreateNotificationsResponse {
  success: true;
  results: BulkCreateNotificationsRowResult[];
  summary: {
    created: number;
    unknownAudience: number;
    invalidSchedule: number;
    failed: number;
  };
}

// ---------------------------------------------------------------------------
// bulkResolveNotifications - preview pass for the admin import dialog.
// Same input as bulkCreate. Does NOT write. Per row, reports whether
// the typed audience resolves to a known per-event audience.
// ---------------------------------------------------------------------------

export const bulkResolveNotificationsInputSchema =
  bulkCreateNotificationsInputSchema;

export type BulkResolveNotificationsInput = z.infer<
  typeof bulkResolveNotificationsInputSchema
>;

export type BulkResolveNotificationsRowStatus =
  | 'new'
  | 'unknown_audience'
  | 'invalid_schedule'
  | 'failed';

export interface BulkResolveNotificationsProposal {
  /** Resolved audience document ID. Populated when status === 'new'. */
  audienceId?: string;
  /** Display name of the matched audience. */
  audienceName?: string;
  /** Final title the row would land at (verbatim from the typed value). */
  canonicalTitle: string;
}

export interface BulkResolveNotificationsRowResult {
  index: number;
  typedTitle: string;
  typedAudience: string;
  status: BulkResolveNotificationsRowStatus;
  proposal?: BulkResolveNotificationsProposal;
  message?: string;
}

export interface BulkResolveNotificationsResponse {
  success: true;
  results: BulkResolveNotificationsRowResult[];
  summary: {
    new: number;
    unknownAudience: number;
    invalidSchedule: number;
    failed: number;
  };
}
